// /src/layouts/Layout.tsx

import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { useAuth } from '../hooks/useAuth';
import { Helmet } from 'react-helmet-async';
import type { UserProfile, UserPlan } from '@/types';
import { normalizeUserPlan } from '../utils/userPlan';
import AppBackground, { type AppBackgroundVariant } from '../components/common/AppBackground';
import { parseCrawlLocale } from '../utils/localePath';
import { apiClient } from '../api/apiClient';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DynamicPageMeta from '../pages/DynamicPageMeta';
import ApiWakeBanner from '../components/common/ApiWakeBanner';
import { GA_FUNNEL, trackEvent } from '../utils/gaEvents';
import { resolveCheckoutCurrency } from '../utils/checkoutCurrency';
import { consumeAuthReturnTo, stashAuthReturnTo } from '../utils/authReturnTo';
import { clearAgenticSessionDraft } from '../config/agenticIntents';
import ProductFeedback from '../components/common/ProductFeedback';
import BlogPostSkeleton from '../components/common/BlogPostSkeleton';
import RouteLoadingShell from '../components/common/RouteLoadingShell';

export interface AppOutletContext {
    isAuthenticated: boolean;
    user: any;
    userPlan: UserPlan;
    userProfile: UserProfile | null;
    handleLogin: () => void;
    handleLogout: () => void;
    refreshProfile: () => Promise<boolean>;
}

const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.architectiq.io/#organization",
    "name": "Architect-IQ",
    "alternateName": ["ArchitectIQ", "Architect IQ", "Arch IQ", "arch iq"],
    "url": "https://www.architectiq.io/",
    "logo": "https://www.architectiq.io/logo192.png",
    "image": "https://www.architectiq.io/og-image.png",
    "sameAs": [
        "https://twitter.com/architect_iq"
    ]
};

/** Cheap paint on the time-to-first-diagram routes. Other shells keep the glow orbs. */
export function canvasBackgroundVariant(pathname: string): AppBackgroundVariant {
    const logical = parseCrawlLocale(pathname).path;
    if (
        logical === '/' ||
        logical === '/start' ||
        logical === '/agentic' ||
        logical.startsWith('/agentic/')
    ) {
        return 'landing';
    }
    return 'full';
}

const Layout: React.FC = () => {
    const { instance, inProgress } = useMsal();
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n, t } = useTranslation();
    const { isAuthenticated, user, loading, userProfile, refreshProfile, getAccessToken } = useAuth();
    const loginKickRef = useRef(false);
    const returnConsumedRef = useRef(false);

    const handleLogin = useCallback(() => {
        stashAuthReturnTo(`${window.location.pathname}${window.location.search}`);
        instance.loginRedirect(loginRequest).catch(e => {
            console.error("Login error:", e);
        });
    }, [instance]);

    const handleLogout = useCallback(() => {
        sessionStorage.removeItem('wizardState');
        clearAgenticSessionDraft();

        if (process.env.NODE_ENV === 'development' && localStorage.getItem('mockUser') === 'true') {
            localStorage.setItem('mockUser', 'false');
            window.location.reload();
            return;
        }

        navigate('/');
        instance.logoutRedirect().catch(e => console.error(e));
    }, [instance, navigate]);

    // ProtectedRoute / Paywall: bounce to home with login_request → start Entra
    useEffect(() => {
        const state = location.state as { login_request?: boolean } | null;
        if (!state?.login_request) return;
        if (isAuthenticated || loading || inProgress !== 'none') return;
        if (loginKickRef.current) return;
        loginKickRef.current = true;
        navigate(location.pathname + location.search, { replace: true, state: {} });
        toast(t('auth.login_required', 'Sign in to continue.'), { icon: '🔐' });
        instance.loginRedirect(loginRequest).catch((e) => console.error('Login redirect failed:', e));
    }, [location.state, location.pathname, location.search, isAuthenticated, loading, inProgress, navigate, instance, t]);

    // After login, return to stashed path (dashboard / agentic / …)
    useEffect(() => {
        if (!isAuthenticated || loading || inProgress !== 'none') return;
        if (returnConsumedRef.current) return;
        const pendingCheckout = localStorage.getItem('pendingCheckout');
        if (pendingCheckout === 'Pro') return; // checkout funnel owns navigation
        const ret = consumeAuthReturnTo();
        if (!ret) return;
        returnConsumedRef.current = true;
        const here = `${location.pathname}${location.search}`;
        if (ret !== here) {
            navigate(ret, { replace: true });
        }
    }, [isAuthenticated, loading, inProgress, navigate, location.pathname, location.search]);

    // --- REFACTORED: INTENT PRESERVATION & AUTO-CHECKOUT ---
    useEffect(() => {
        const processPendingCheckout = async () => {
            // Czekamy na pełną stabilność stanu autoryzacji i profilu użytkownika
            if (isAuthenticated && inProgress === 'none' && !loading && userProfile) {

                // 1. Odczyt z trwałego cache (odporne na Incognito)
                const pendingCheckout = localStorage.getItem('pendingCheckout');

                if (pendingCheckout === 'Pro') {
                    // 2. Destructive Read: natychmiastowe czyszczenie zapobiega pętlom i przeterminowanym intencjom
                    localStorage.removeItem('pendingCheckout');

                    // 3. Walidacja biznesowa
                    if (userProfile.plan === 'Pro' || userProfile.plan === 'Enterprise') {
                        toast.success('You already have an active PRO subscription! Welcome back.');
                        return; // Przerwanie lejka, użytkownik jest już premium
                    }

                    // 4. Inicjacja zakupu
                    try {
                        toast.loading('Redirecting to secure checkout...', { duration: 2000 });
                        trackEvent(GA_FUNNEL.CHECKOUT_STARTED, { plan: 'Pro', source: 'pending_login', currency: resolveCheckoutCurrency(i18n.language) });

                        const token = await getAccessToken();
                        const response = await apiClient.post('/api/stripe/create-checkout', {
                            planType: 'Pro',
                            lang: i18n.language,
                            currency: resolveCheckoutCurrency(i18n.language),
                        }, { token });

                        if (response && response.url) {
                            window.location.assign(response.url);
                        }
                    } catch (error) {
                        console.error("Auto-checkout after login failed:", error);
                        toast.error('Failed to initiate checkout. Please try again from the Pricing page.');
                    }
                }
            }
        };

        processPendingCheckout();
    }, [isAuthenticated, inProgress, loading, userProfile, getAccessToken, i18n.language]);

    // 1. NAJPIERW definiujemy zmienne i Hooki (useMemo)
    const computedUserPlan = normalizeUserPlan(userProfile?.plan);

    const contextValue: AppOutletContext = useMemo(() => ({
        isAuthenticated,
        user,
        userPlan: computedUserPlan,
        userProfile,
        handleLogin,
        handleLogout,
        refreshProfile
    }), [isAuthenticated, user, computedUserPlan, userProfile, handleLogin, handleLogout, refreshProfile]);

    // Public pages must paint without waiting for MSAL/profile (landing LCP).
    // Dashboard stays gated by ProtectedRoute.
    // Suspense around Outlet (not whole Routes) keeps Navbar/footer while lazy chunks load.
    const logicalPath = parseCrawlLocale(location.pathname).path;
    const outletFallback = logicalPath.startsWith('/blog/')
        ? <BlogPostSkeleton />
        : <RouteLoadingShell variant="main" />;

    return (
        <>
            <DynamicPageMeta />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(organizationSchema)}
                </script>
            </Helmet>

            <AppBackground variant={canvasBackgroundVariant(location.pathname)} />

            <div className="min-h-[100dvh] flex flex-col font-sans text-white selection:bg-primary/30 selection:text-white relative z-10">
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-lg focus:bg-[#111827] focus:px-4 focus:py-2 focus:text-sm focus:text-white focus:ring-2 focus:ring-primary-light focus:outline-none"
                >
                    {t('common.skip_to_content')}
                </a>

                <Navbar isAuthenticated={isAuthenticated} onLogin={handleLogin} onLogout={handleLogout} />

                <main id="main-content" tabIndex={-1} className="flex-grow flex flex-col relative w-full outline-none">
                    <Suspense fallback={outletFallback}>
                        <Outlet context={contextValue} />
                    </Suspense>
                </main>

                <ProductFeedback />
                <SiteFooter />
                <ApiWakeBanner />
            </div>
        </>
    );
};

export default Layout;

export function useAppOutlet() {
    return useOutletContext<AppOutletContext>();
}
